// src/pages/NewMeeting.js
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { doc, getDoc, addDoc, collection, serverTimestamp, query, where, getDocs } from 'firebase/firestore/lite';
import { db } from '../firebase/config';
import { useAuth } from '../context/AuthContext';
import PageLoading from '../components/PageLoading';

export default function NewMeeting() {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const teamId = searchParams.get('teamId');

  const [team, setTeam] = useState(null);
  const [memberProfiles, setMemberProfiles] = useState({}); // email -> displayName
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [meetingName, setMeetingName] = useState('');
  const [meetingType, setMeetingType] = useState('Tactical');
  const [meetingDate, setMeetingDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedAttendees, setSelectedAttendees] = useState([]);
  const [customAttendee, setCustomAttendee] = useState('');
  const [extraAttendees, setExtraAttendees] = useState([]);

  useEffect(() => {
    if (teamId) loadTeam();
    else setLoading(false);
  }, [teamId]);

  async function loadTeam() {
    try {
      const snap = await getDoc(doc(db, 'teams', teamId));
      if (snap.exists()) {
        const data = { id: snap.id, ...snap.data() };
        setTeam(data);
        setSelectedAttendees(data.memberEmails || []);

        // Fetch display names for all team members
        const emails = data.memberEmails || [];
        if (emails.length > 0) {
          const usersSnap = await getDocs(
            query(collection(db, 'users'), where('email', 'in', emails))
          );
          const profiles = {};
          usersSnap.forEach(d => {
            const u = d.data();
            if (u.email) profiles[u.email] = u.displayName || u.email;
          });
          setMemberProfiles(profiles);
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  const toggleAttendee = (email) => {
    setSelectedAttendees(prev =>
      prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]
    );
  };

  const addCustomAttendee = () => {
    if (!customAttendee.trim()) return;
    setExtraAttendees(prev => [...prev, customAttendee.trim()]);
    setCustomAttendee('');
  };

  const removeExtra = (name) => {
    setExtraAttendees(prev => prev.filter(n => n !== name));
  };

  const allAttendees = [
    ...selectedAttendees.map(email => ({
      id: email,
      name: email,
      displayName: memberProfiles[email] || email.split('@')[0],
      type: 'member',
    })),
    ...extraAttendees.map(name => ({ id: name, name, displayName: name, type: 'guest' })),
  ];

  const handleStart = async () => {
    if (!meetingName.trim()) return setError('Please enter a meeting name.');
    if (allAttendees.length === 0) return setError('Please select at least one attendee.');
    setSaving(true);
    setError('');

    try {
      const meetingRef = await addDoc(collection(db, 'meetings'), {
        name: meetingName.trim(),
        meetingType,
        teamId: teamId || null,
        teamName: team?.name || null,
        consultantId: currentUser.uid,
        consultantName: userProfile?.displayName || userProfile?.email,
        meetingDate: new Date(meetingDate),
        attendees: allAttendees,
        entries: [],
        status: 'draft',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      navigate(`/meetings/${meetingRef.id}/record`);
    } catch (err) {
      console.error(err);
      setError('Failed to create meeting. Please try again.');
      setSaving(false);
    }
  };

  if (loading) return <PageLoading text="Loading team..." />;

  return (
    <div className="fade-up" style={{ maxWidth: 680 }}>
      <div className="page-header">
        <h2>New Meeting Observation</h2>
        <p>{team?.name ? `${team.name} — set up the meeting details before you start recording.` : 'Set up the meeting details and add attendees before you start recording.'}</p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Meeting details */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-title">Meeting Details</div>

        <div className="form-group">
          <label>Meeting Name</label>
          <input
            type="text"
            value={meetingName}
            onChange={e => setMeetingName(e.target.value)}
            placeholder="e.g. Weekly Team Standup, Q2 Planning Session"
            autoFocus
          />
        </div>

        <div className="form-group">
          <label>Meeting Type</label>
          <select
            value={meetingType}
            onChange={e => setMeetingType(e.target.value)}
            style={{ padding: '10px 14px', border: '1.5px solid var(--border)', borderRadius: 8, fontFamily: 'var(--font-body)', fontSize: '0.875rem', color: 'var(--ink)', background: 'var(--bg-card)', width: '100%', cursor: 'pointer' }}
          >
            <option value="Check In">Check In</option>
            <option value="Tactical">Tactical</option>
            <option value="Strategic">Strategic</option>
            <option value="Off-site">Off-site</option>
          </select>
        </div>

        <div className="form-group">
          <label>Date</label>
          <input
            type="date"
            value={meetingDate}
            onChange={e => setMeetingDate(e.target.value)}
          />
        </div>
      </div>

      {/* Attendees */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-title">Attendees</div>
        <div className="card-subtitle">Check off who is present in this meeting.</div>

        {/* Team members */}
        {team?.memberEmails?.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-muted)', marginBottom: 12, fontWeight: 600 }}>
              Team Members
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {team.memberEmails.map(email => (
                <label key={email} style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '10px 14px',
                  borderRadius: 8,
                  border: `1.5px solid ${selectedAttendees.includes(email) ? 'var(--accent)' : 'var(--border)'}`,
                  background: selectedAttendees.includes(email) ? 'var(--accent-glow)' : 'transparent',
                  cursor: 'pointer',
                  transition: 'var(--transition)',
                }}>
                  <input
                    type="checkbox"
                    checked={selectedAttendees.includes(email)}
                    onChange={() => toggleAttendee(email)}
                    style={{ accentColor: 'var(--accent)', width: 16, height: 16 }}
                  />
                  <span style={{ fontSize: '0.875rem' }}>{memberProfiles[email] || email}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Add guest */}
        <div>
          <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-muted)', marginBottom: 12, fontWeight: 600 }}>
            {teamId ? 'Add Guest / External Attendee' : 'Add Attendees'}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <input
              type="text"
              value={customAttendee}
              onChange={e => setCustomAttendee(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addCustomAttendee()}
              placeholder="Name or email"
              style={{ flex: 1, padding: '10px 14px', border: '1.5px solid var(--border)', borderRadius: 8, fontFamily: 'var(--font-body)', fontSize: '0.875rem' }}
            />
            <button className="btn btn-secondary" onClick={addCustomAttendee}>Add</button>
          </div>
          {extraAttendees.length > 0 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
              {extraAttendees.map(name => (
                <span key={name} style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '4px 10px', borderRadius: 100,
                  background: 'var(--bg-soft)', fontSize: '0.8rem',
                }}>
                  {name}
                  <button onClick={() => removeExtra(name)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-muted)', fontSize: '1rem', lineHeight: 1 }}>×</button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Summary */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: '0.875rem', color: 'var(--ink-muted)' }}>
          {allAttendees.length} attendee{allAttendees.length !== 1 ? 's' : ''} selected
        </span>
        <button className="btn btn-primary" onClick={handleStart} disabled={saving}>
          {saving ? <><span className="spinner" /> Creating...</> : 'Create Meeting & Start Recording →'}
        </button>
      </div>
    </div>
  );
}